import { of, map, tap, catchError, Observable } from 'rxjs';

import { Router, UrlTree } from '@angular/router';
import { inject, Injectable } from '@angular/core';

import { TokenStorage } from '@/app/core/services';

import { Profile, ProfileApi } from '@/app/entities/profile';

import { ToastService } from '@/app/shared/components/ui';

import { EnsuredAuth } from './types';

import { AuthApi } from './auth-api';
import { AuthStore } from './auth-store';
import { Login, Credentials } from './models';

import { handleError } from '@/app/shared/helpers';
import { extractDataNonNull } from '@/app/shared/http';

@Injectable({
  providedIn: 'root',
})
export class AuthFacade {
  private readonly router = inject(Router);
  private readonly authApi = inject(AuthApi);
  private readonly authStore = inject(AuthStore);
  private readonly profileApi = inject(ProfileApi);
  private readonly toastService = inject(ToastService);
  private readonly tokenStorage = inject(TokenStorage);

  private loggingOut = false;

  ensureAuth$(): Observable<EnsuredAuth> {
    const status = this.authStore.status();

    if (status === 'authed') {
      return of<EnsuredAuth>('authed');
    }

    if (!this.tokenStorage.has()) {
      return of<EnsuredAuth>('guest');
    }

    return this.hydrateCurrentUser().pipe(
      map(() => (this.authStore.status() === 'authed' ? 'authed' : 'guest')),
      catchError(() => of<EnsuredAuth>('guest')),
    );
  }

  redirectForAuthed(): UrlTree {
    return this.router.createUrlTree(['/']);
  }

  redirectForGuest(): UrlTree {
    return this.router.createUrlTree(['/auth/login']);
  }

  navigateAfterLogin(): Promise<boolean> {
    return this.router.navigate(['/parents']);
  }

  hydrateCurrentUser(): Observable<Profile | null> {
    if (!this.tokenStorage.has()) {
      this.authStore.setGuest();
      return of(null);
    }
    return this.profileApi.getProfile().pipe(
      extractDataNonNull(),
      tap((profile) => this.authStore.setAuthUser(profile)),
      catchError(() => {
        this.clearSession();
        return of(null);
      }),
    );
  }

  login(data: Login): Observable<Credentials> {
    return this.authApi.login(data).pipe(
      extractDataNonNull(),
      tap((credentials) => {
        if (credentials.profile) {
          this.authStore.setAuthUser(credentials.profile);
        }
        this.tokenStorage.set(credentials.token);
      }),
      catchError(handleError),
    );
  }

  refreshToken(): Observable<string> {
    return this.authApi.refreshToken().pipe(
      extractDataNonNull(),
      tap((credentials) => this.tokenStorage.set(credentials.token)),
      map((credentials) => credentials.token),
      catchError(handleError),
    );
  }

  forceLogout(reason: 'session-expired' | 'manual' = 'manual'): void {
    if (this.loggingOut) {
      return;
    }
    this.loggingOut = true;
    this.authApi.logout().subscribe();

    this.authStore.setAuthUser(null);
    this.tokenStorage.clear();

    this.router.navigateByUrl('/auth/login').then(
      () => {
        const message = reason === 'session-expired' ? 'Your session has timed-out. Please login again.' : null;

        if (message) {
          this.toastService.error(message);
        }
        this.loggingOut = false;
      },
      () => {
        this.loggingOut = false;
      },
    );
  }

  private clearSession(): void {
    this.tokenStorage.clear();
    this.authStore.setGuest();
  }

  forgotPassword(email: string): Observable<string> {
    return this.authApi.forgotPassword(email).pipe(catchError(handleError));
  }
}
